import {
    AfterViewInit,
    Component,
    ElementRef,
    HostListener,
    inject,
    Input,
    LOCALE_ID,
    OnDestroy,
    OnInit,
    output,
    Renderer2
} from '@angular/core';
import { FormControl, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { DateModel, dateModelsEqual, dateModelToString } from 'app/shared/date/date.model';
import { OverlayRef, POSITION_TARGET, PositionTarget } from 'app/shared/ui/overlays';


const getLocaleMonthNames = (locale: string, style: 'long' | 'short' | 'narrow' = 'long'): string[] => {
    const formatter = new Intl.DateTimeFormat(locale, { month: style });
    return [...Array(12).keys()].map(i => formatter.format(new Date(2000, i, 1)));
};


const getLocaleDayNames = (locale: string, style: 'long' | 'short' | 'narrow' = 'long'): string[] => {
    const formatter = new Intl.DateTimeFormat(locale, { weekday: style });
    return [...Array(7).keys()].map(i => formatter.format(new Date(2000, 0, 2 + i)));
};


const getLocaleFirstDayOfWeek = (_locale: string): number => {
    // "new Intl.Locale(locale).getWeekInfo().firstDay % 7" is not production ready, always return SUN for now
    return 0;
};


const dateToDateModel = (date: Date) => ({ day: date.getDate(), month: date.getMonth(), year: date.getFullYear() });

const isYearValid = (year: number) => {
    const date = new Date();
    date.setFullYear(+year);
    return date.getFullYear() === +year;
};

const yearValidator: ValidatorFn = control => isYearValid(control.value) ? null : { 'invalid-year': true };


@Component({
    selector: 'ui-calendar',
    imports: [
        ReactiveFormsModule,
    ],
    host: {
        'animate.enter': 'calendar-enter',
        'animate.leave': 'calendar-leave',
    },
    styles: [
        `
        :host {
            display: inline-block;
            z-index: 1060;
        }
        @keyframes calendar-fade-in {
            from { opacity: 0; }
        }
        @keyframes calendar-fade-out {
            to { opacity: 0; }
        }
        :host.calendar-enter {
            animation: calendar-fade-in 0.15s ease-in;
        }
        :host.calendar-leave {
            animation: calendar-fade-out 0.15s ease-out forwards;
        }
        @keyframes calendar-left {
            50% { transform: translateX(105%); }
            51% { transform: translateX(-130%); }
            to { transform: translateX(0); }
        }
        @keyframes calendar-right {
            50% { transform: translateX(-105%); }
            51% { transform: translateX(130%); }
            to { transform: translateX(0); }
        }
        .month.left {
            animation: calendar-left 180ms linear;
        }
        .month.right {
            animation: calendar-right 180ms linear;
        }
        .calendar {
            overflow: hidden;
            background-color: white;
            border-radius: 5px;
            user-select: none;
        }
        .header {
            display: flex;
            flex-wrap: nowrap;
            justify-content: space-between;
            padding: 0.5rem;
            text-align: center;
        }
        .header .calendar-spacer {
            flex: 1 1 auto;
        }
        .header .year {
            display: inline-block;
            width: 3em;
            padding: 2px 4px;
            border: 1px solid transparent;
            border-radius: 2px;
            font-size: 1em;
            transition: 0.32s;
        }
        .header .year:focus {
            outline: none;
        }
        .header .year:focus.ng-valid {
            border-color: var(--success);
        }
        .header .year:focus.ng-invalid {
            border-color: var(--danger);
        }

        .arrow-month,
        .arrow-section {
            color: #B3D0E2;
            cursor: pointer;
            background: transparent;
            border: none;
            outline: none;
            transition: 0.37s;
        }
        .arrow-month:hover,
        .arrow-section:hover {
            color: #1D2531;
        }

        .content {
            padding: 1em;
        }

        .labels, .value-row {
            display: flex;
            flex-wrap: nowrap;
            justify-content: space-around;
        }

        .cell {
            display: inline-block;
            flex-grow: 1;
            flex-basis: 0;
            margin: 0.3rem;
            padding: 0.3rem;
            text-align: center;
        }

        .label {
            color: #B3D0E2;
            font-size: x-small;
            text-transform: uppercase;
        }

        .year-selection {
            border-radius: 50%;
            color: #1D2531;
            cursor: pointer;
        }
        .year-selection.current {
            color: #1D2531;
            background: #D8E7F0;
        }
        .year-selection.active,
        .year-selection:active {
            color: white;
            background: linear-gradient(to bottom, #339ceb, #1583d6);
        }

        .day {
            border-radius: 50%;
            color: #B3D0E2;
            cursor: pointer;
        }
        .day.current {
            color: #1D2531;
        }

        .day.today {
            color: #1D2531;
            background: #D8E7F0;
        }

        .day.active,
        .day:active {
            color: white;
            background: linear-gradient(to bottom, #339ceb, #1583d6);
        }

        .buttons {
            display: flex;
            justify-content: space-between;
        }
        .buttons .btn-link {
            color: #B3D0E2;
        }
        .buttons .btn-link:hover {
            cursor: pointer;
            color: #1D2531;
        }
        `,
    ],
    template: `
        <div class="calendar border shadow">
            @if (calendarSection === 'days') {
                <div class="header border-bottom">
                    <div>
                        <span class="pl-1">{{ currentMonthName }}</span>
                        <input
                            #yearInput
                            class="year"
                            placeholder="Year"
                            [formControl]="yearControl"
                            (keyup.enter)="yearInput.blur()"
                            (blur)="changeYear()">
                        <button type="button" class="arrow-section" (click)="onChangeSection('days')">
                            <i class="fa fa-caret-down"></i>
                        </button>
                    </div>
                    <div class="text-align-center pr-2">
                        <button type="button" class="arrow-month" (click)="updateValue('month' ,-1)">
                            <i class="fa fa-lg fa-caret-left"></i>
                        </button>
                        <button type="button" class="arrow-month" (click)="updateValue('month', +1)">
                            <i class="fa fa-lg fa-caret-right"></i>
                        </button>
                    </div>
                </div>
                <div class="content">
                    <div class="labels">
                        @for (day of dayNames; track $index) {
                            <span class="label cell">{{ day }}</span>
                        }
                    </div>
                    <div class="month" [class.left]="animate === 'left'" [class.right]="animate === 'right'" (animationend)="stopAnimation()">
                        @for (week of calendarWeeks; track identifyWeek(week)) {
                            <div class="value-row">
                                @for (day of week; track identifyDay(day)) {
                                    <button
                                        type="button"
                                        class="btn btn-link day cell"
                                        [class.current]="isCurrentMonth(day)"
                                        [class.active]="isChosenDay(day)"
                                        [class.today]="isToday(day)"
                                        (click)="onSelectDay(day)">
                                        {{ day.day }}
                                    </button>
                                }
                            </div>
                        }
                    </div>
                    <div class="buttons">
                        <button type="button" (click)="setToday()" class="btn btn-link">Today</button>
                        <button type="button" (click)="resetValue()" class="btn btn-link">Reset</button>
                    </div>
                </div>
            }
            @else if (calendarSection === 'years') {
                <div class="header">
                    <div>
                        <span class="pl-1">{{ currentMonthName + ' ' + currentYear }}</span>
                        <button type="button" class="arrow-section" (click)="onChangeSection('years')">
                            <i class="fa fa-caret-up"></i>
                        </button>
                    </div>
                    <div class="calendar-spacer"></div>
                    <div class="text-align-center pr-2">
                        <button type="button" class="arrow-month" (click)="updateYearsSelection('left')">
                            <i class="fa fa-lg fa-caret-left"></i>
                        </button>
                        <button type="button" class="arrow-month" (click)="updateYearsSelection('right')">
                            <i class="fa fa-lg fa-caret-right"></i>
                        </button>
                    </div>
                </div>
                <div class="content">
                    <div class="month" [class.left]="animate === 'left'" [class.right]="animate === 'right'" (animationend)="stopAnimation()">
                        @for (rowOfYears of calendarYears; track $index) {
                            <div class="value-row">
                                @for (year of rowOfYears; track year) {
                                    <button
                                        type="button"
                                        class="btn btn-link year-selection cell"
                                        [class.current]="isCurrentYear(year)"
                                        [class.active]="isChosenYear(year)"
                                        (click)="onSelectYear(year)">
                                        {{ year.year }}
                                    </button>
                                }
                            </div>
                        }
                    </div>
                </div>
            }
            @else if (calendarSection === 'months') {
                <div class="header">
                    <div>
                        <span class="pl-1">{{ selectedYear.year }}</span>
                        <button type="button" class="arrow-section" (click)="onChangeSection('months')">
                            <i class="fa fa-caret-up"></i>
                        </button>
                    </div>
                    <div class="calendar-spacer"></div>
                    <div class="text-align-center pr-2">
                        <button type="button" class="arrow-month" (click)="updateValue('year', -1)">
                            <i class="fa fa-lg fa-caret-left"></i>
                        </button>
                        <button type="button" class="arrow-month" (click)="updateValue('year', +1)">
                            <i class="fa fa-lg fa-caret-right"></i>
                        </button>
                    </div>
                </div>
                <div class="content">
                    <div class="month" [class.left]="animate === 'left'" [class.right]="animate === 'right'" (animationend)="stopAnimation()">
                        @for (row of calendarMonths; track row) {
                            <div class="value-row">
                                @for (month of row; track month) {
                                    <button
                                        type="button"
                                        class="btn btn-link year-selection cell"
                                        [class.current]="isCurrentMonth(month)"
                                        [class.active]="isChosenMonth(month)"
                                        (click)="onSelectMonth(month)">
                                        {{ showMonthAbbr(month.month) }}
                                    </button>
                                }
                            </div>
                        }
                    </div>
                </div>
            }
        </div>
    `,
})
export class CalendarComponent implements OnInit, AfterViewInit, OnDestroy {

    locale = inject<string>(LOCALE_ID);

    calendarSection: string;

    calendarWeeks: DateModel[][];

    calendarYears: DateModel[][];

    calendarMonths: DateModel[][];

    currentYear: number;

    selectedYear: DateModel;

    monthNames = [...getLocaleMonthNames(this.locale, 'long')];

    currentMonthName: string;

    selectedMonth: DateModel;

    firstDayOfWeek = getLocaleFirstDayOfWeek(this.locale);

    dayNames = [...getLocaleDayNames(this.locale, 'short')];

    animate: string;

    yearControl = new FormControl('', [Validators.required, yearValidator]);

    private animationFrame: any;

    private _date: DateModel | null;

    get date() {
        return this._date;
    }

    @Input()
    set date(date: DateModel | null) {
        this._date = date;
        if (date !== null) {
            this.setCurrentValues(date.year, date.month);
        }
    }

    dateChange = output<DateModel | null>();

    private _currentMonth: number;

    get currentMonth(): number {
        return this._currentMonth;
    }

    set currentMonth(month: number) {
        this._currentMonth = month;
        this.currentMonthName = this.monthNames[month];
    }

    private today = dateToDateModel(new Date());

    private overlayRef = inject(OverlayRef, { optional: true });

    private elementRef = inject(ElementRef);

    private renderer = inject(Renderer2);

    private parent = inject<PositionTarget>(POSITION_TARGET, { optional: true });

    ngOnInit() {
        this.calendarSection = 'days';
        if (!this.date) {
            // show calendar for today, do not pre-select anything
            this.setCurrentValues(this.today.year, this.today.month);
        }
    }

    ngAfterViewInit() {
        if (this.parent) {
            this.startCalculatingPosition();
        }
    }

    onChangeSection(section: string) {
        // User can return to years section from months section
        switch(section) {
            case 'months': {
                this.calendarSection = 'years';
                break;
            }
            default: {
                this.calendarSection = section === 'years' ? 'days' : 'years';
            }
        }

        if (this.calendarSection === 'years') {
            const year = this.selectedYear ? this.selectedYear.year : this.currentYear;
            this.updateCalendar('years', year, this.currentMonth);
        }
    }

    onSelectYear(year: DateModel) {
        this.selectedYear = year;
        this.prepareMonthSelection(year);
        this.calendarSection = 'months';
    }

    private prepareMonthSelection(dateModel: DateModel) {
        const date = new Date(dateModel.year, dateModel.month);
        const monthsSelection: DateModel[] = [];
        for (let i = 0; i < this.monthNames.length; i++) {
            date.setMonth(i);
            monthsSelection.push(dateToDateModel(date));
        }
        this.calendarMonths = this.sliceIntoRows(monthsSelection, 4);
    }

    showMonthAbbr(month: number) {
        const monthName = this.monthNames[month];
        return monthName.slice(0, 3).toUpperCase();
    }

    onSelectMonth(month: DateModel) {
        this.selectedMonth = month;
        this.calendarSection = 'days';
        this.setNewDate();
    }

    private setNewDate(day?: DateModel | null) {
        const newDate = day ? new Date(this.selectedMonth.year, this.selectedMonth.month, day.day)
            : new Date(this.selectedMonth.year, this.selectedMonth.month);
        this.date = dateToDateModel(newDate);
        this.dateChange.emit(this.date);
    }

    updateValue(valueType: 'month' |  'year', relativeValue: number) {
        switch(valueType) {
            case 'month': {
                let newYear = this.currentYear;
                let newMonth = this.currentMonth + relativeValue;
                if (newMonth > 11) {
                    newMonth = 0;
                    newYear += 1;
                } else if (newMonth < 0) {
                    newMonth = 11;
                    newYear -= 1;
                }
                this.triggerAnimation(relativeValue > 0 ? 'right' : 'left');
                this.setCurrentValues(newYear, newMonth);
                break;
            }
            case 'year': {
                this.selectedYear.year = this.selectedYear.year + relativeValue;
                break;
            }
        }
    }

    changeYear() {
        const newYear = +(this.yearControl.value || '');
        if (this.yearControl.valid && newYear !== this.currentYear) {
            this.triggerAnimation(newYear < this.currentYear ? 'left' : 'right');
            this.currentYear = newYear;
            this.updateCalendar('days', this.currentYear, this.currentMonth);
        }
    }

    onSelectDay(day: DateModel | null) {
        this.date = day;
        if (this.selectedYear && this.selectedMonth) {
            this.setNewDate(this.date);
        }
        if (this.overlayRef) {
            this.overlayRef.close(this.date);
        }
    }

    setToday() {
        this.onSelectDay(this.today);
    }

    resetValue() {
        this.onSelectDay(null);
    }

    isToday(day: DateModel) {
        return dateModelsEqual(day, this.today);
    }

    isChosenDay(day: DateModel) {
        // ensure [class.active] is false if coming from months section
        if (this.selectedMonth) {
            return false;
        }
        return this.date ? dateModelsEqual(this.date, day) : false;
    }

    identifyDay(day: DateModel) {
        return dateModelToString(day);
    }

    identifyWeek(week: DateModel[]) {
        return dateModelToString(week[0]);
    }

    isCurrentMonth(dateModel: DateModel) {
        if (this.selectedYear) {
            return dateModel.month === this.currentMonth && this.selectedYear.year === this.currentYear;
        }
        return dateModel.month === this.currentMonth;
    }

    isChosenMonth(month: DateModel) {
        return this.date ? dateModelsEqual(this.date, month) : false;
    }

    isCurrentYear(year: DateModel) {
        if (this.selectedYear) {
            return year.year === this.selectedYear.year;
        }
        return year.year === this.currentYear;
    }

    isChosenYear(year: DateModel) {
        return this.date ? dateModelsEqual(this.date, year) : false;
    }

    triggerAnimation(direction: string) {
        this.animate = direction;
    }

    stopAnimation() {
        this.animate = 'reset';
    }

    @HostListener('document:click', ['$event'])
    onClick(event: MouseEvent) {
        if (!this.overlayRef) {
            return;
        }
        if (!(event.target as any).isConnected) {
            return;
        }
        const clickOutside = !this.elementRef.nativeElement.contains(event.target);
        if (clickOutside) {
            this.overlayRef.dismiss('click outside');
        }
    }

    private weekStartDate(firstDayInMonth: Date) {
        if (isNaN(firstDayInMonth.valueOf())) {
            console.error('Invalid date');
        }
        const startDate = new Date(firstDayInMonth);
        while (startDate.getDay() !== this.firstDayOfWeek) {
            startDate.setDate(startDate.getDate() - 1);
        }
        return startDate;
    }

    private updateCalendar(section: 'days' | 'years', year: number, month: number, day = 1) {
        const date = this.weekStartDate(new Date(year, month, day));

        switch(section) {
            case 'days': {
                const weeks: DateModel[][] = [];
                let week: DateModel[];
                while ((date.getMonth() <= month || (date.getMonth() === 11 && month === 0)) && date.getFullYear() <= year) {
                    week = [];
                    for (let i = 0; i < 7; i++) {
                        week.push(dateToDateModel(date));
                        date.setDate(date.getDate() + 1);
                    }
                    weeks.push(week);
                }
                this.calendarWeeks = weeks;
                break;
            }
            case 'years': {
                const yearsSelection: DateModel[] = [];
                // 24 years in yearsSelection
                // Keep current in the middle
                date.setFullYear(date.getFullYear() - 12);
                for (let i = 0; i < 24; i++) {
                    yearsSelection.push(dateToDateModel(date));
                    date.setFullYear(date.getFullYear() + 1);
                }
                this.calendarYears = this.sliceIntoRows(yearsSelection, 4);
            }
        }
    }

    updateYearsSelection(direction: string) {
        switch (direction) {
            case 'left': {
                const firstChunk = this.calendarYears[0];
                const lowestYear = firstChunk[0];
                const newMiddleYear = lowestYear['year'] - 12;
                this.updateCalendar('years', newMiddleYear, lowestYear['month']);
                break;
            }
            case 'right': {
                const lastChunk = this.calendarYears[this.calendarYears.length - 1];
                const highestYear = lastChunk[lastChunk.length - 1];
                const newMiddleYear = highestYear['year'] + 13;
                this.updateCalendar('years', newMiddleYear, highestYear['month']);
                break;
            }
        }
    }

    private sliceIntoRows<T>(arr: T[], rowSize: number) {
        // Slice any array into rows with specified length (rowSize)
        const result: T[][] = [];
        for (let i = 0; i < arr.length; i += rowSize) {
            const chunk = arr.slice(i, i + rowSize);
            result.push(chunk);
        }
        return result;
    }

    private setCurrentValues(year: number, month: number) {
        this.currentMonth = month;
        this.currentYear = year;
        this.yearControl.setValue(`${this.currentYear}`);
        this.updateCalendar('days', this.currentYear, this.currentMonth);
    }

    private startCalculatingPosition() {
        this.calcPosition();
        this.animationFrame = requestAnimationFrame(() => this.startCalculatingPosition());
    }

    private calcPosition() {
        const clientRect = this.parent!.getBoundingClientRect();
        this.renderer.setStyle(this.elementRef.nativeElement, 'position', 'absolute');
        this.renderer.setStyle(this.elementRef.nativeElement, 'top', `${clientRect.bottom}px`);
        this.renderer.setStyle(this.elementRef.nativeElement, 'left', `${clientRect.left}px`);
    }

    ngOnDestroy() {
        cancelAnimationFrame(this.animationFrame);
    }
}
